import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AllMarketsListApiResponse,
  IFetchMarket,
} from '@/services/market/market.type';
import { ApiResponse } from '../sagas/locationSaga';

export interface MarketState {
  markets: ApiResponse;
  loading: boolean;
  error: boolean;
  marketsList: AllMarketsListApiResponse;
  lastSelectedOperatorId?: string;
  message?: string;
}

export const initialState: MarketState = {
  markets: {
    data: [],
    pagination: {
      totalRecords: 0,
      currentPage: 0,
      totalPages: 0,
      nextPage: 0,
      previousPage: 0,
      totalItems: 0,
    },
    success: false,
    message: '',
  },
  loading: false,
  error: false,
  marketsList: {
    success: false,
    message: '',
    data: [],
    pagination: {
      totalRecords: 0,
      currentPage: 0,
      totalPages: 0,
      nextPage: 0,
      previousPage: 0,
    },
  },
  lastSelectedOperatorId: undefined,
};

const marketSlice = createSlice({
  name: 'market',
  initialState,
  reducers: {
    getMarkets: (state, action: PayloadAction<IFetchMarket>) => {
      state.loading = true;
    },
    getMarketsSuccess: (state, action: PayloadAction<ApiResponse>) => {
      state.loading = false;
      state.markets = { ...action.payload };
    },
    getMarketsFailure: (state, action: any) => {
      state.loading = false;
      state.error = true;
      state.markets = { ...initialState?.markets };
      state.message = action.payload?.messageDetails?.length
        ? action.payload?.messageDetails[0]?.message
        : action.payload?.message;
    },
    // List API
    getMarketsList: (state, action: PayloadAction<IFetchMarket>) => {
      state.loading = true;
      state.message = '';
    },
    getMarketsListSuccess: (
      state,
      action: PayloadAction<
        AllMarketsListApiResponse & { operatorId?: string }
      >,
    ) => {
      state.loading = false;
      const { data, pagination, success, message, operatorId } = action.payload;
      const isSameOperator = state.lastSelectedOperatorId === operatorId;
      const isNextPage = pagination?.currentPage > 1 && isSameOperator;
      state.error = false;
      state.message = action.payload.message;
      state.marketsList = {
        success,
        message,
        pagination,
        data: isNextPage ? [...state.marketsList.data, ...data] : data,
      };

      state.lastSelectedOperatorId = operatorId;
    },
    getMarketsListFailure: (state, action: any) => {
      state.loading = false;
      state.error = true;
      state.message = action.payload?.messageDetails?.length
        ? action.payload?.messageDetails[0]?.message
        : action.payload?.message;
    },
    clearMarketError: (state) => {
      state.error = false;
      state.message = ''; // Reset message to empty string as per initial state
    },
  },
});

const marketReducer = marketSlice.reducer;

export const {
  getMarkets,
  getMarketsSuccess,
  getMarketsFailure,
  getMarketsList,
  getMarketsListSuccess,
  getMarketsListFailure,
  clearMarketError,
} = marketSlice.actions;

export default marketReducer;
